package mx.lebencore.portico.core

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import portico.composeapp.generated.resources.Res
import portico.composeapp.generated.resources.ic_add
import portico.composeapp.generated.resources.ic_alarm
import portico.composeapp.generated.resources.ic_assignment
import portico.composeapp.generated.resources.ic_check_circle
import portico.composeapp.generated.resources.ic_document
import portico.composeapp.generated.resources.ic_email
import portico.composeapp.generated.resources.ic_home
import portico.composeapp.generated.resources.ic_map
import portico.composeapp.generated.resources.ic_notification
import portico.composeapp.generated.resources.ic_route
import portico.composeapp.generated.resources.ic_search
import portico.composeapp.generated.resources.ic_tools
import portico.composeapp.generated.resources.ic_truck
import portico.composeapp.generated.resources.ic_warning

@Composable
fun PorticoApp(windowWidth: Dp = 1366.dp) {
    val windowSize = remember(windowWidth) { PorticoWindowSize.fromWidth(windowWidth) }
    var currentScreen by remember { mutableStateOf(PorticoScreen.Dashboard) }
    var selectedTrip by remember { mutableStateOf(trips.first()) }

    MaterialTheme {
        Surface(modifier = Modifier.fillMaxSize(), color = AppColors.Background) {
            when (windowSize) {
                PorticoWindowSize.Compact -> CompactPorticoLayout(currentScreen) { currentScreen = it }
                PorticoWindowSize.Medium -> MediumPorticoLayout(currentScreen) { currentScreen = it }
                PorticoWindowSize.Expanded -> DesktopPorticoLayout(
                    currentScreen = currentScreen,
                    selectedTrip = selectedTrip,
                    onScreenSelected = { currentScreen = it },
                    onTripSelected = {
                        selectedTrip = it
                        currentScreen = PorticoScreen.TripDetail
                    },
                )
            }
        }
    }
}

object AppColors {
    val Background = Color(0xFFF1F5F9)
    val SidebarBorder = Color(0xFFE2E8F0)
    val CardBorder = Color(0xFFE2E8F0)
    val Text = Color(0xFF0F172A)
    val Muted = Color(0xFF475569)
    val MutedSoft = Color(0xFF94A3B8)
    val Dark = Color(0xFF0F172A)
    val White = Color.White
    val Soft = Color(0xFFF8FAFC)
}

enum class PorticoWindowSize {
    Compact,
    Medium,
    Expanded;

    companion object {
        fun fromWidth(width: Dp): PorticoWindowSize = when {
            width < 720.dp -> Compact
            width < 1100.dp -> Medium
            else -> Expanded
        }
    }
}

sealed class PorticoIcon {
    data object Add : PorticoIcon()
    data object Alarm : PorticoIcon()
    data object Assignment : PorticoIcon()
    data object CheckCircle : PorticoIcon()
    data object Document : PorticoIcon()
    data object Email : PorticoIcon()
    data object Home : PorticoIcon()
    data object Map : PorticoIcon()
    data object Notification : PorticoIcon()
    data object Route : PorticoIcon()
    data object Search : PorticoIcon()
    data object Tools : PorticoIcon()
    data object Truck : PorticoIcon()
    data object Warning : PorticoIcon()
}

enum class PorticoScreen(val label: String, val icon: PorticoIcon) {
    Dashboard("Dashboard", PorticoIcon.Home),
    Trips("Viajes", PorticoIcon.Route),
    TripDetail("Detalle del viaje", PorticoIcon.Assignment),
    Documents("Documentos", PorticoIcon.Document),
    Units("Unidades", PorticoIcon.Truck),
    Alerts("Alertas", PorticoIcon.Notification),
    Phase2("Fase 2 · Mecánica", PorticoIcon.Tools),
    Phase3("Fase 3 · GPS", PorticoIcon.Map),
}

enum class TripFilter(val label: String) {
    All("Todos"),
    Active("Activos"),
    Incidents("Incidencias"),
    Delivered("Entregados")
}

@Composable
private fun DesktopPorticoLayout(
    currentScreen: PorticoScreen,
    selectedTrip: TripUiModel,
    onScreenSelected: (PorticoScreen) -> Unit,
    onTripSelected: (TripUiModel) -> Unit,
) {
    Row(modifier = Modifier.fillMaxSize()) {
        Sidebar(
            currentScreen = currentScreen,
            onScreenSelected = onScreenSelected,
            modifier = Modifier.width(288.dp).fillMaxHeight(),
        )
        Column(
            modifier = Modifier.weight(1f).fillMaxHeight().verticalScroll(rememberScrollState()).padding(32.dp),
        ) {
            DesktopScreenContent(currentScreen, selectedTrip, onScreenSelected, onTripSelected)
        }
    }
}

@Composable
private fun MediumPorticoLayout(
    currentScreen: PorticoScreen,
    onScreenSelected: (PorticoScreen) -> Unit,
) {
    Row(modifier = Modifier.fillMaxSize()) {
        Sidebar(
            currentScreen = currentScreen,
            onScreenSelected = onScreenSelected,
            compact = true,
            modifier = Modifier.width(96.dp).fillMaxHeight(),
        )
        Column(
            modifier = Modifier.weight(1f).fillMaxHeight().verticalScroll(rememberScrollState()).padding(24.dp),
        ) {
            CompactScreenContent(currentScreen, onScreenSelected)
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun CompactPorticoLayout(
    currentScreen: PorticoScreen,
    onScreenSelected: (PorticoScreen) -> Unit,
) {
    Column(modifier = Modifier.fillMaxSize()) {
        TopAppBar(
            title = {
                Column {
                    Text("LEBENCORE", fontSize = 11.sp, letterSpacing = 1.8.sp, color = AppColors.Muted)
                    Text("PÓRTICO", fontWeight = FontWeight.SemiBold)
                }
            },
            actions = {
                IconButton(onClick = {}) {
                    PorticoIconView(PorticoIcon.Notification, null, AppColors.Text)
                }
            },
        )
        Column(
            modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            MobileScreenSelector(currentScreen, onScreenSelected)
            CompactScreenContent(currentScreen, onScreenSelected)
        }
    }
}

@Composable
private fun Sidebar(
    currentScreen: PorticoScreen,
    onScreenSelected: (PorticoScreen) -> Unit,
    modifier: Modifier = Modifier,
    compact: Boolean = false,
) {
    Column(
        modifier = modifier.background(Color.White).border(1.dp, AppColors.SidebarBorder).padding(12.dp),
    ) {
        if (compact) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                contentAlignment = Alignment.Center,
            ) {
                LogoBadge()
            }
        } else {
            Column(modifier = Modifier.fillMaxWidth().padding(8.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    LogoBadge()
                    Column {
                        Text("LEBENCORE", fontSize = 11.sp, letterSpacing = 2.6.sp, color = AppColors.Muted)
                        Text("PÓRTICO", fontSize = 28.sp, lineHeight = 30.sp, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
                    }
                }
                Spacer(Modifier.height(12.dp))
                Text(
                    "Panel operativo para viajes, cartas porte, unidades y monitoreo modular.",
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.Muted,
                )
            }
            HorizontalDivider(color = AppColors.SidebarBorder)
        }

        Spacer(Modifier.height(8.dp))

        PorticoScreen.entries.forEach { screen ->
            val active = currentScreen == screen
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(20.dp))
                    .background(if (active) AppColors.Dark else Color.Transparent)
                    .clickable { onScreenSelected(screen) }
                    .padding(horizontal = 12.dp, vertical = 14.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = if (compact) Arrangement.Center else Arrangement.Start,
            ) {
                PorticoIconView(screen.icon, screen.label, if (active) Color.White else Color(0xFF334155), Modifier.size(18.dp))
                if (!compact) {
                    Spacer(Modifier.width(12.dp))
                    Text(
                        screen.label,
                        color = if (active) Color.White else Color(0xFF334155),
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Medium,
                    )
                }
            }
            Spacer(Modifier.height(4.dp))
        }
    }
}

@Composable
private fun LogoBadge() {
    Surface(
        color = AppColors.Dark,
        shape = RoundedCornerShape(18.dp),
        shadowElevation = 2.dp,
    ) {
        Box(modifier = Modifier.size(44.dp), contentAlignment = Alignment.Center) {
            PorticoIconView(PorticoIcon.Truck, null, Color.White, Modifier.size(20.dp))
        }
    }
}

@Composable
private fun MobileScreenSelector(
    currentScreen: PorticoScreen,
    onScreenSelected: (PorticoScreen) -> Unit,
) {
    Row(modifier = Modifier.horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        listOf(
            PorticoScreen.Dashboard,
            PorticoScreen.Trips,
            PorticoScreen.Documents,
            PorticoScreen.Units,
            PorticoScreen.Alerts,
        ).forEach { screen ->
            val active = currentScreen == screen
            Row(
                modifier = Modifier.clip(RoundedCornerShape(16.dp)).background(if (active) AppColors.Dark else Color.White)
                    .border(if (active) 0.dp else 1.dp, if (active) Color.Transparent else AppColors.CardBorder, RoundedCornerShape(16.dp))
                    .clickable { onScreenSelected(screen) }
                    .padding(horizontal = 12.dp, vertical = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                PorticoIconView(screen.icon, null, if (active) Color.White else AppColors.Muted, Modifier.size(16.dp))
                Spacer(Modifier.width(8.dp))
                Text(screen.label, color = if (active) Color.White else AppColors.Text, fontSize = 13.sp)
            }
            Spacer(Modifier.width(8.dp))
        }
    }
}

@Composable
private fun DesktopScreenContent(
    currentScreen: PorticoScreen,
    selectedTrip: TripUiModel,
    onScreenSelected: (PorticoScreen) -> Unit,
    onTripSelected: (TripUiModel) -> Unit,
) {
    when (currentScreen) {
        PorticoScreen.Dashboard -> DashboardScreen(onScreenSelected, onTripSelected)
        PorticoScreen.Trips -> TripsScreen(onScreenSelected, onTripSelected)
        PorticoScreen.TripDetail -> DetailScreen(selectedTrip)
        PorticoScreen.Documents -> DocumentsScreen()
        PorticoScreen.Units -> UnitsScreen()
        PorticoScreen.Alerts -> AlertsScreen()
        PorticoScreen.Phase2 -> PlaceholderScreen(
            title = "Fase 2 · Captura mecánica y scanner",
            subtitle = "Integración con lectura de datos del tracto y captura operativa del viaje.",
            icon = PorticoIcon.Tools,
            points = listOf(
                "Scanner operativo basado en proyecto open source",
                "Lectura de viaje, kilometraje, consumo y velocidad máxima",
                "Captura de incidencias mecánicas y evidencias fotográficas",
                "Checklist pre-viaje y post-viaje por operador",
            ),
        )
        PorticoScreen.Phase3 -> PlaceholderScreen(
            title = "Fase 3 · Monitoreo GPS y control",
            subtitle = "Trazabilidad en tiempo real, alertas y reglas automáticas de operación.",
            icon = PorticoIcon.Map,
            points = listOf(
                "Mapa en tiempo real y geocercas",
                "Alertas por desvío, detención y exceso de velocidad",
                "Timeline del viaje con eventos operativos",
                "Base para funciones avanzadas de control y seguridad",
            ),
        )
    }
}

@Composable
private fun CompactScreenContent(
    currentScreen: PorticoScreen,
    onScreenSelected: (PorticoScreen) -> Unit,
) {
    when (currentScreen) {
        PorticoScreen.Dashboard -> DashboardScreen(onScreenSelected) { }
        PorticoScreen.Trips -> TripsScreen(onScreenSelected) { }
        PorticoScreen.TripDetail -> DetailScreen(trips.first())
        PorticoScreen.Documents -> DocumentsScreen()
        PorticoScreen.Units -> UnitsScreen()
        PorticoScreen.Alerts -> AlertsScreen()
        PorticoScreen.Phase2 -> PlaceholderScreen("Fase 2 · Captura mecánica y scanner", "Integración con lectura de datos del tracto y captura operativa del viaje.", PorticoIcon.Tools, listOf("Scanner operativo basado en proyecto open source", "Lectura de viaje, kilometraje, consumo y velocidad máxima", "Captura de incidencias mecánicas y evidencias fotográficas", "Checklist pre-viaje y post-viaje por operador"))
        PorticoScreen.Phase3 -> PlaceholderScreen("Fase 3 · Monitoreo GPS y control", "Trazabilidad en tiempo real, alertas y reglas automáticas de operación.", PorticoIcon.Map, listOf("Mapa en tiempo real y geocercas", "Alertas por desvío, detención y exceso de velocidad", "Timeline del viaje con eventos operativos", "Base para funciones avanzadas de control y seguridad"))
    }
}

@Composable
private fun DashboardScreen(
    onScreenSelected: (PorticoScreen) -> Unit,
    onTripSelected: (TripUiModel) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        SectionHeader(
            title = "Dashboard operativo",
            subtitle = "Vista general de viajes, pendientes documentales y estado de la flota.",
            action = {
                PrimaryButton("Nuevo viaje", PorticoIcon.Add) { onScreenSelected(PorticoScreen.Trips) }
            },
        )

        FlowRow(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            MetricCard("Viajes activos", "12", "4 en tránsito · 3 con incidencia", PorticoIcon.Route)
            MetricCard("Cartas por validar", "3", "Evitar salida con expediente incompleto", PorticoIcon.Assignment)
            MetricCard("Unidades con alerta", "2", "1 mantenimiento · 1 incidencia mecánica", PorticoIcon.Truck)
            MetricCard("Usuarios activos", "4", "Operación pequeña, proceso centralizado", PorticoIcon.Home)
        }

        Row(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalAlignment = Alignment.Top, modifier = Modifier.fillMaxWidth()) {
            Column(modifier = Modifier.weight(1.4f)) {
                AppCard(title = "Viajes recientes") {
                    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        trips.forEach { trip ->
                            DashboardTripCard(trip = trip, onOpen = { onTripSelected(trip) })
                        }
                    }
                }
            }
            Column(modifier = Modifier.weight(0.9f)) {
                AppCard(title = "Alertas clave") {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        alerts.forEach { alert ->
                            AlertListItem(alert)
                        }
                        Surface(color = AppColors.Soft, shape = RoundedCornerShape(18.dp)) {
                            Column(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
                                Text("Siguiente paso sugerido", fontWeight = FontWeight.Medium, color = AppColors.Text)
                                Spacer(Modifier.height(8.dp))
                                Text(
                                    "Activar prevalidación documental para bloquear salidas con carta porte incompleta.",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = AppColors.Muted,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TripsScreen(
    onScreenSelected: (PorticoScreen) -> Unit,
    onTripSelected: (TripUiModel) -> Unit,
) {
    var search by remember { mutableStateOf("") }
    var filter by remember { mutableStateOf(TripFilter.All) }

    val visibleTrips = remember(search, filter) {
        trips.filter { trip ->
            val matchesSearch = search.isBlank() || listOf(trip.client, trip.unit, trip.carta, trip.id).any { it.contains(search, true) }
            val matchesFilter = when (filter) {
                TripFilter.All -> true
                TripFilter.Active -> trip.status in listOf("En tránsito", "Programado", "Con incidencia")
                TripFilter.Incidents -> trip.status == "Con incidencia"
                TripFilter.Delivered -> trip.status == "Entregado"
            }
            matchesSearch && matchesFilter
        }
    }

    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        SectionHeader(
            title = "Viajes",
            subtitle = "Expediente digital del viaje con filtros, estados y acciones rápidas.",
            action = {
                PrimaryButton("Crear viaje", PorticoIcon.Add) {}
            },
        )

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SearchField(search) { search = it }
            Spacer(Modifier.width(16.dp))
            FilterTabs(current = filter, onChange = { filter = it })
        }

        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            visibleTrips.forEach { trip ->
                TripListCard(
                    trip = trip,
                    onPdf = { onScreenSelected(PorticoScreen.Documents) },
                    onOpen = { onTripSelected(trip) },
                )
            }
        }
    }
}

@Composable
private fun DetailScreen(trip: TripUiModel) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        SectionHeader(
            title = "Detalle del viaje · ${trip.id}",
            subtitle = "Expediente central del viaje: operación, documentación y trazabilidad.",
            action = {
                PrimaryButton("Marcar como entregado", PorticoIcon.CheckCircle) {}
            },
        )

        Row(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalAlignment = Alignment.Top, modifier = Modifier.fillMaxWidth()) {
            Column(modifier = Modifier.weight(1.2f), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                AppCard(title = "Resumen del viaje") {
                    FlowRow(horizontalArrangement = Arrangement.spacedBy(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        InfoBlock("Cliente", trip.client)
                        InfoBlock("Carta porte", trip.carta)
                        InfoBlock("Unidad", trip.unit)
                        InfoBlock("Operador", trip.operator)
                        InfoBlock("Ruta", trip.route)
                        InfoBlock("Contenedores", trip.containers)
                        InfoBlock("Chasis", "CH-5520")
                        InfoBlock("Dolly", trip.dolly)
                    }
                }

                AppCard(title = "Línea de tiempo") {
                    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        listOf(
                            "08:05" to "Viaje creado y asignado",
                            "08:22" to "Carta porte generada",
                            "08:29" to "Correo enviado al cliente",
                            "09:11" to "Unidad en salida",
                            "12:40" to "En tránsito · actualización de estatus",
                        ).forEach { (time, event) ->
                            TimelineItem(time, event)
                        }
                    }
                }
            }

            Column(modifier = Modifier.weight(0.8f), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                AppCard(title = "Checklist de salida") {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        listOf(
                            "Cliente asignado",
                            "Unidad confirmada",
                            "Operador asignado",
                            "Carta porte generada",
                            "Expediente documental completo",
                        ).forEach { item ->
                            ChecklistItem(item)
                        }
                    }
                }

                AppCard(title = "Acciones rápidas") {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        OutlineActionButton("Regenerar PDF", PorticoIcon.Document) {}
                        OutlineActionButton("Reenviar por correo", PorticoIcon.Email) {}
                        OutlineActionButton("Actualizar estatus", PorticoIcon.Alarm) {}
                        OutlineActionButton("Registrar incidencia", PorticoIcon.Warning) {}
                    }
                }
            }
        }
    }
}

@Composable
private fun DocumentsScreen() {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        SectionHeader(
            title = "Documentos y carta porte",
            subtitle = "Generación instantánea de PDF, plantillas y envío por correo.",
            action = {
                PrimaryButton("Generar PDF", PorticoIcon.Document) {}
            },
        )

        Row(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalAlignment = Alignment.Top, modifier = Modifier.fillMaxWidth()) {
            Column(modifier = Modifier.weight(1f)) {
                AppCard(title = "Vista previa del expediente") {
                    Surface(
                        modifier = Modifier.fillMaxWidth().height(420.dp),
                        color = AppColors.Soft,
                        shape = RoundedCornerShape(28.dp),
                    ) {
                        Column(modifier = Modifier.fillMaxSize().border(1.dp, AppColors.CardBorder, RoundedCornerShape(28.dp)).padding(24.dp)) {
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                Column {
                                    Text("PÓRTICO", fontSize = 11.sp, letterSpacing = 2.6.sp, color = AppColors.Muted)
                                    Spacer(Modifier.height(4.dp))
                                    Text("Carta porte · CP-009231", fontSize = 20.sp, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
                                }
                                InverseBadge("Borrador validado")
                            }
                            Spacer(Modifier.height(16.dp))
                            HorizontalDivider(color = AppColors.CardBorder)
                            Spacer(Modifier.height(24.dp))
                            FlowRow(horizontalArrangement = Arrangement.spacedBy(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                                InfoBlock("Cliente", "Transportes del Norte")
                                InfoBlock("Ruta", "Monterrey → Nuevo Laredo")
                                InfoBlock("Unidad", "TR-18")
                                InfoBlock("Operador", "Luis Herrera")
                                InfoBlock("Contenedores", "CNT-8821, CNT-8822")
                                InfoBlock("Dolly", "DL-14")
                            }
                            Spacer(Modifier.height(24.dp))
                            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                listOf(
                                    "Datos del cliente completos",
                                    "Ruta y destino confirmados",
                                    "Unidad y operador asignados",
                                    "Número de carta porte capturado",
                                ).forEach { rule ->
                                    Surface(color = Color.White, shape = RoundedCornerShape(18.dp), modifier = Modifier.fillMaxWidth()) {
                                        Row(
                                            modifier = Modifier.fillMaxWidth().border(1.dp, AppColors.CardBorder, RoundedCornerShape(18.dp)).padding(horizontal = 16.dp, vertical = 14.dp),
                                            horizontalArrangement = Arrangement.SpaceBetween,
                                            verticalAlignment = Alignment.CenterVertically,
                                        ) {
                                            Text(rule, fontSize = 14.sp, color = AppColors.Text)
                                            PorticoIconView(PorticoIcon.CheckCircle, null, Color(0xFF059669), Modifier.size(16.dp))
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            Column(modifier = Modifier.weight(0.9f)) {
                AppCard(title = "Automatización documental") {
                    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        FeaturePanel("Plantillas disponibles", "Carta porte, orden de viaje, resumen operativo, correo al cliente.")
                        FeaturePanel("Reglas de validación", "Bloquear salida si faltan campos clave del expediente.")
                        FeaturePanel("Envío inmediato", "Correo al cliente y al equipo con adjuntos y trazabilidad.")
                        PrimaryButton("Enviar expediente", PorticoIcon.Email, modifier = Modifier.fillMaxWidth()) {}
                    }
                }
            }
        }
    }
}

@Composable
private fun UnitsScreen() {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        SectionHeader(
            title = "Unidades",
            subtitle = "Preparación para mantenimiento, incidencias y monitoreo futuro.",
            action = { PrimaryButton("Agregar unidad", PorticoIcon.Add) {} },
        )

        FlowRow(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            units.forEach { unit ->
                Surface(
                    modifier = Modifier.widthIn(min = 280.dp, max = 360.dp),
                    color = Color.White,
                    shape = RoundedCornerShape(28.dp),
                    shadowElevation = 2.dp,
                ) {
                    Column(modifier = Modifier.fillMaxWidth().padding(20.dp), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                            Text(unit.id, fontSize = 22.sp, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
                            StatusBadge(unit.status)
                        }
                        InfoBlock("Modelo", unit.model)
                        InfoBlock("Kilometraje", unit.kilometers)
                        InfoBlock("Próximo servicio", unit.nextService)
                    }
                }
            }
        }
    }
}

@Composable
private fun AlertsScreen() {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        SectionHeader(
            title = "Alertas e incidencias",
            subtitle = "Eventos clave para operación, documentos y mantenimiento.",
        )
        FlowRow(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            alerts.forEach { alert ->
                Surface(color = Color.White, shape = RoundedCornerShape(28.dp), shadowElevation = 2.dp, modifier = Modifier.widthIn(min = 360.dp, max = 520.dp)) {
                    Row(modifier = Modifier.fillMaxWidth().padding(20.dp), verticalAlignment = Alignment.Top) {
                        Surface(color = Color(0xFFFEF3C7), shape = RoundedCornerShape(18.dp)) {
                            Box(modifier = Modifier.size(40.dp), contentAlignment = Alignment.Center) {
                                PorticoIconView(PorticoIcon.Warning, null, Color(0xFFB45309), Modifier.size(18.dp))
                            }
                        }
                        Spacer(Modifier.width(12.dp))
                        Column(modifier = Modifier.weight(1f)) {
                            Text(alert.title, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
                            Spacer(Modifier.height(8.dp))
                            Text(alert.text, style = MaterialTheme.typography.bodySmall, color = AppColors.Muted)
                            Spacer(Modifier.height(12.dp))
                            Text("Prioridad ${alert.level}", fontSize = 12.sp, color = AppColors.Muted)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PlaceholderScreen(
    title: String,
    subtitle: String,
    icon: PorticoIcon,
    points: List<String>,
) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        SectionHeader(title = title, subtitle = subtitle)
        AppCard(title = null) {
            Column(verticalArrangement = Arrangement.spacedBy(24.dp), modifier = Modifier.padding(8.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                    Surface(color = Color(0xFFF1F5F9), shape = RoundedCornerShape(24.dp)) {
                        Box(modifier = Modifier.size(56.dp), contentAlignment = Alignment.Center) {
                            PorticoIconView(icon, null, Color(0xFF334155), Modifier.size(24.dp))
                        }
                    }
                    Column {
                        Text("Módulo previsto", fontSize = 22.sp, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
                        Text("Espacio reservado para la siguiente evolución del producto.", style = MaterialTheme.typography.bodySmall, color = AppColors.Muted)
                    }
                }
                FlowRow(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    points.forEach { point ->
                        Surface(color = Color.White, shape = RoundedCornerShape(18.dp)) {
                            Text(
                                point,
                                modifier = Modifier.border(1.dp, AppColors.CardBorder, RoundedCornerShape(18.dp)).padding(16.dp).widthIn(min = 260.dp, max = 420.dp),
                                fontSize = 14.sp,
                                color = Color(0xFF334155),
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SectionHeader(
    title: String,
    subtitle: String,
    action: (@Composable () -> Unit)? = null,
) {
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
        Column(modifier = Modifier.weight(1f)) {
            Text(title, fontSize = 32.sp, lineHeight = 36.sp, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
            Spacer(Modifier.height(4.dp))
            Text(subtitle, fontSize = 14.sp, color = AppColors.Muted)
        }
        if (action != null) {
            Spacer(Modifier.width(16.dp))
            action()
        }
    }
}

@Composable
private fun MetricCard(
    title: String,
    value: String,
    hint: String,
    icon: PorticoIcon,
) {
    Surface(
        modifier = Modifier.widthIn(min = 250.dp, max = 320.dp),
        color = Color.White,
        shape = RoundedCornerShape(28.dp),
        shadowElevation = 2.dp,
    ) {
        Row(modifier = Modifier.fillMaxWidth().padding(20.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
            Column(modifier = Modifier.weight(1f)) {
                Text(title, fontSize = 14.sp, color = Color(0xFF64748B))
                Spacer(Modifier.height(8.dp))
                Text(value, fontSize = 30.sp, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
                Spacer(Modifier.height(8.dp))
                Text(hint, fontSize = 14.sp, color = Color(0xFF64748B))
            }
            Surface(color = Color(0xFFF1F5F9), shape = RoundedCornerShape(18.dp)) {
                Box(modifier = Modifier.size(44.dp), contentAlignment = Alignment.Center) {
                    PorticoIconView(icon, null, Color(0xFF334155), Modifier.size(20.dp))
                }
            }
        }
    }
}

@Composable
private fun DashboardTripCard(trip: TripUiModel, onOpen: () -> Unit) {
    Surface(color = Color.White, shape = RoundedCornerShape(22.dp)) {
        Column(modifier = Modifier.fillMaxWidth().border(1.dp, AppColors.CardBorder, RoundedCornerShape(22.dp)).padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Column(modifier = Modifier.weight(1f)) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text(trip.id, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
                        StatusBadge(trip.status)
                    }
                    Spacer(Modifier.height(4.dp))
                    Text("${trip.client} · ${trip.route}", fontSize = 14.sp, color = AppColors.Muted)
                }
                OutlinedButton(onClick = onOpen, shape = RoundedCornerShape(18.dp)) {
                    Text("Ver detalle", color = AppColors.Text)
                }
            }
            FlowRow(horizontalArrangement = Arrangement.spacedBy(24.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                InfoBlock("Unidad", trip.unit)
                InfoBlock("Operador", trip.operator)
                InfoBlock("Carta porte", trip.carta)
                InfoBlock("ETA", trip.eta)
            }
            ProgressBar(trip.progress)
        }
    }
}

@Composable
private fun TripListCard(
    trip: TripUiModel,
    onPdf: () -> Unit,
    onOpen: () -> Unit,
) {
    Surface(color = Color.White, shape = RoundedCornerShape(28.dp), shadowElevation = 2.dp) {
        Column(modifier = Modifier.fillMaxWidth().padding(20.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Column(modifier = Modifier.weight(1f)) {
                    FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text(trip.id, fontSize = 18.sp, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
                        StatusBadge(trip.status)
                        OutlineBadge("Prioridad ${trip.priority}")
                    }
                    Spacer(Modifier.height(8.dp))
                    Text("${trip.client} · ${trip.route}", fontSize = 14.sp, color = AppColors.Muted)
                }
                FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlineActionButton("PDF", PorticoIcon.Document, onPdf)
                    OutlineActionButton("Correo", PorticoIcon.Email) {}
                    PrimaryButton("Abrir viaje", null, onClick = onOpen)
                }
            }
            FlowRow(horizontalArrangement = Arrangement.spacedBy(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                InfoBlock("Unidad", trip.unit)
                InfoBlock("Operador", trip.operator)
                InfoBlock("Carta porte", trip.carta)
                InfoBlock("Contenedores", trip.containers)
                InfoBlock("Dolly", trip.dolly)
                InfoBlock("ETA", trip.eta)
            }
        }
    }
}

@Composable
private fun SearchField(value: String, onValueChange: (String) -> Unit) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = Modifier.widthIn(min = 320.dp, max = 380.dp),
        shape = RoundedCornerShape(18.dp),
        singleLine = true,
        leadingIcon = { PorticoIconView(PorticoIcon.Search, null, AppColors.Muted, Modifier.size(16.dp)) },
        placeholder = { Text("Buscar por cliente, unidad, carta porte...", fontSize = 14.sp) },
    )
}

@Composable
private fun FilterTabs(current: TripFilter, onChange: (TripFilter) -> Unit) {
    Surface(color = Color.White, shape = RoundedCornerShape(18.dp), modifier = Modifier.border(1.dp, AppColors.CardBorder, RoundedCornerShape(18.dp))) {
        Row(modifier = Modifier.padding(4.dp), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            TripFilter.entries.forEach { item ->
                val active = item == current
                Box(
                    modifier = Modifier.clip(RoundedCornerShape(14.dp)).background(if (active) Color(0xFFF1F5F9) else Color.Transparent).clickable { onChange(item) }.padding(horizontal = 14.dp, vertical = 10.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(item.label, fontSize = 13.sp, fontWeight = FontWeight.Medium, color = AppColors.Text)
                }
            }
        }
    }
}

@Composable
private fun ProgressBar(progress: Int) {
    Box(modifier = Modifier.fillMaxWidth().height(8.dp).clip(RoundedCornerShape(99.dp)).background(Color(0xFFE2E8F0))) {
        Box(
            modifier = Modifier.fillMaxWidth(progress / 100f).height(8.dp).clip(RoundedCornerShape(99.dp)).background(Color(0xFF0F172A)),
        )
    }
}

@Composable
private fun TimelineItem(time: String, event: String) {
    Row(verticalAlignment = Alignment.Top, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        Box(modifier = Modifier.padding(top = 4.dp).size(10.dp).background(AppColors.Dark, RoundedCornerShape(99.dp)))
        Column {
            Text(event, fontSize = 14.sp, fontWeight = FontWeight.Medium, color = AppColors.Text)
            Text(time, fontSize = 12.sp, color = Color(0xFF64748B))
        }
    }
}

@Composable
private fun ChecklistItem(label: String) {
    Surface(color = Color.White, shape = RoundedCornerShape(18.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth().border(1.dp, AppColors.CardBorder, RoundedCornerShape(18.dp)).padding(horizontal = 12.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(label, fontSize = 14.sp, color = Color(0xFF334155))
            PorticoIconView(PorticoIcon.CheckCircle, null, Color(0xFF059669), Modifier.size(16.dp))
        }
    }
}

@Composable
private fun AlertListItem(alert: AlertUiModel) {
    Surface(color = Color.White, shape = RoundedCornerShape(18.dp)) {
        Column(modifier = Modifier.fillMaxWidth().border(1.dp, AppColors.CardBorder, RoundedCornerShape(18.dp)).padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                PorticoIconView(PorticoIcon.Warning, null, Color(0xFFD97706), Modifier.size(16.dp))
                Text(alert.title, fontWeight = FontWeight.Medium, color = AppColors.Text)
            }
            Spacer(Modifier.height(8.dp))
            Text(alert.text, fontSize = 14.sp, color = AppColors.Muted)
            Spacer(Modifier.height(8.dp))
            Text("Prioridad ${alert.level}", fontSize = 12.sp, color = Color(0xFF64748B))
        }
    }
}

@Composable
private fun FeaturePanel(title: String, text: String) {
    Surface(color = Color.White, shape = RoundedCornerShape(18.dp)) {
        Column(modifier = Modifier.fillMaxWidth().border(1.dp, AppColors.CardBorder, RoundedCornerShape(18.dp)).padding(16.dp)) {
            Text(title, fontWeight = FontWeight.Medium, color = AppColors.Text)
            Spacer(Modifier.height(8.dp))
            Text(text, fontSize = 14.sp, color = AppColors.Muted)
        }
    }
}

@Composable
private fun AppCard(
    title: String?,
    content: @Composable ColumnScope.() -> Unit,
) {
    Surface(color = Color.White, shape = RoundedCornerShape(28.dp), shadowElevation = 2.dp) {
        Column(modifier = Modifier.fillMaxWidth().padding(20.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            if (title != null) {
                Text(title, fontSize = 20.sp, fontWeight = FontWeight.SemiBold, color = AppColors.Text)
            }
            content()
        }
    }
}

@Composable
private fun PrimaryButton(
    label: String,
    icon: PorticoIcon? = null,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Button(onClick = onClick, shape = RoundedCornerShape(18.dp), modifier = modifier) {
        if (icon != null) {
            PorticoIconView(icon, null, Color.White, Modifier.size(16.dp))
            Spacer(Modifier.width(8.dp))
        }
        Text(label)
    }
}

@Composable
private fun OutlineActionButton(label: String, icon: PorticoIcon, onClick: () -> Unit) {
    OutlinedButton(onClick = onClick, shape = RoundedCornerShape(18.dp)) {
        PorticoIconView(icon, null, AppColors.Text, Modifier.size(16.dp))
        Spacer(Modifier.width(8.dp))
        Text(label, color = AppColors.Text)
    }
}

@Composable
private fun StatusBadge(text: String) {
    val background = when (text) {
        "En tránsito" -> Color(0xFFDBEAFE)
        "Programado" -> Color(0xFFFEF3C7)
        "Con incidencia" -> Color(0xFFFEE2E2)
        "Entregado" -> Color(0xFFD1FAE5)
        "Activa" -> Color(0xFFD1FAE5)
        "Programada" -> Color(0xFFFEF3C7)
        "Alerta" -> Color(0xFFFEE2E2)
        else -> Color(0xFFE2E8F0)
    }
    val content = when (text) {
        "En tránsito" -> Color(0xFF1D4ED8)
        "Programado" -> Color(0xFFB45309)
        "Con incidencia" -> Color(0xFFB91C1C)
        "Entregado" -> Color(0xFF047857)
        "Activa" -> Color(0xFF047857)
        "Programada" -> Color(0xFFB45309)
        "Alerta" -> Color(0xFFB91C1C)
        else -> Color(0xFF334155)
    }
    Surface(color = background, shape = RoundedCornerShape(12.dp)) {
        Text(text, modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp), fontSize = 12.sp, color = content, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun OutlineBadge(text: String) {
    Surface(color = Color.Transparent, shape = RoundedCornerShape(12.dp), modifier = Modifier.border(1.dp, AppColors.CardBorder, RoundedCornerShape(12.dp))) {
        Text(text, modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp), fontSize = 12.sp, color = AppColors.Text)
    }
}

@Composable
private fun InverseBadge(text: String) {
    Surface(color = AppColors.Dark, shape = RoundedCornerShape(12.dp)) {
        Text(text, modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp), fontSize = 12.sp, color = Color.White, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun InfoBlock(label: String, value: String) {
    Column {
        Text(label, fontSize = 12.sp, color = AppColors.MutedSoft)
        Spacer(Modifier.height(4.dp))
        Text(value, fontSize = 14.sp, fontWeight = FontWeight.Medium, color = AppColors.Text)
    }
}

@Composable
private fun PorticoIconView(
    icon: PorticoIcon,
    contentDescription: String?,
    tint: Color,
    modifier: Modifier = Modifier.size(20.dp),
) {
    val resource: DrawableResource = when (icon) {
        PorticoIcon.Add -> Res.drawable.ic_add
        PorticoIcon.Alarm -> Res.drawable.ic_alarm
        PorticoIcon.Assignment -> Res.drawable.ic_assignment
        PorticoIcon.CheckCircle -> Res.drawable.ic_check_circle
        PorticoIcon.Document -> Res.drawable.ic_document
        PorticoIcon.Email -> Res.drawable.ic_email
        PorticoIcon.Home -> Res.drawable.ic_home
        PorticoIcon.Map -> Res.drawable.ic_map
        PorticoIcon.Notification -> Res.drawable.ic_notification
        PorticoIcon.Route -> Res.drawable.ic_route
        PorticoIcon.Search -> Res.drawable.ic_search
        PorticoIcon.Tools -> Res.drawable.ic_tools
        PorticoIcon.Truck -> Res.drawable.ic_truck
        PorticoIcon.Warning -> Res.drawable.ic_warning
    }

    Icon(
        painter = painterResource(resource),
        contentDescription = contentDescription,
        tint = tint,
        modifier = modifier,
    )
}

data class TripUiModel(
    val id: String,
    val client: String,
    val unit: String,
    val operator: String,
    val route: String,
    val carta: String,
    val status: String,
    val containers: String,
    val dolly: String,
    val progress: Int,
    val eta: String,
    val priority: String,
)

data class UnitUiModel(
    val id: String,
    val model: String,
    val status: String,
    val kilometers: String,
    val nextService: String,
)

data class AlertUiModel(
    val title: String,
    val text: String,
    val level: String,
)

private val trips = listOf(
    TripUiModel("VJ-24031", "Transportes del Norte", "TR-18", "Luis Herrera", "Monterrey → Nuevo Laredo", "CP-009231", "En tránsito", "CNT-8821, CNT-8822", "DL-14", 72, "Hoy 18:40", "Alta"),
    TripUiModel("VJ-24032", "Logística Rivera", "TR-07", "Marco Soto", "Saltillo → Querétaro", "CP-009232", "Programado", "CNT-7712", "—", 18, "Mañana 09:20", "Media"),
    TripUiModel("VJ-24033", "Carga Industrial MX", "TR-11", "Elena Cruz", "Monterrey → San Luis Potosí", "CP-009233", "Con incidencia", "CNT-1931", "DL-08", 55, "Pendiente", "Alta"),
    TripUiModel("VJ-24034", "Aceros del Bajío", "TR-02", "Raúl Pérez", "Apodaca → León", "CP-009234", "Entregado", "CNT-6601", "—", 100, "Completado", "Baja"),
)

private val units = listOf(
    UnitUiModel("TR-18", "Kenworth T680", "Activa", "324,900", "En 2,300 km"),
    UnitUiModel("TR-07", "Freightliner Cascadia", "Programada", "412,110", "En 900 km"),
    UnitUiModel("TR-11", "International LT", "Alerta", "287,100", "Revisar hoy"),
)

private val alerts = listOf(
    AlertUiModel("Viaje VJ-24033 con incidencia", "Se detectó retraso y observación mecánica pendiente.", "Alta"),
    AlertUiModel("Carta porte faltante por validar", "2 viajes no tienen expediente completo para salida.", "Media"),
    AlertUiModel("Mantenimiento próximo TR-07", "Programar servicio preventivo esta semana.", "Media"),
)